import axios from 'axios';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import UserContext from '../../context/UserContext/UserContext';
import useAbortController from '../../hooks/useAbortController';
import AuthService from '../../services/AuthService/AuthService';
import UserService from '../../services/UserService/UserService';

function RequireNoAuth(): React.ReactElement | null {
  const { user, setUser } = useContext(UserContext);
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(() => !!AuthService.getToken());

  const { getAbortSignal } = useAbortController();

  const fetchUser = useCallback(async (): Promise<void> => {
    if (!AuthService.getToken()) {
      setIsLoading(false);
      return;
    }

    try {
      const signal = getAbortSignal();
      const fetchedUser = await UserService.loggedInUser(signal);
      setUser(fetchedUser);
    } catch (error: any) {
      if (axios.isCancel(error)) return;
      setUser(null);
      if (error.response?.status === 401) {
        await AuthService.logout();
      }
    } finally {
      setIsLoading(false);
    }
  }, [setUser, setIsLoading, getAbortSignal]);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  if (isLoading) {
    return (
      <div className='flex h-screen w-full items-center justify-center bg-background'>
        <Loader2 className='h-8 w-8 animate-spin text-primary' />
      </div>
    );
  }

  if (user?.mfaComplete) {
    const redirectTo = location.state?.from?.pathname || '/chats';
    return <Navigate to={redirectTo} replace />;
  }

  return <Outlet />;
}

export default RequireNoAuth;
